/**
 * Duplicate detection for imported statement rows.
 *
 * A row is a "duplicate" when the same (bank, date, description, amount)
 * already exists in the store — either from a previous upload of the same
 * statement or from an overlapping period on a different file.
 *
 * Occurrence counting: if the DB already holds one copy of a row and the
 * incoming statement contains it twice, one copy is kept. This prevents
 * wrongly collapsing legitimate repeats (two identical same-day purchases)
 * while still stopping re-uploads from double-counting.
 */

/** Normalised identity key for a transaction row. */
export function txKey(bank: string, postedAt: string, description: string, amountCents: number): string {
  const desc = description.toLowerCase().replace(/\s+/g, ' ').trim();
  return `${bank}\u0000${postedAt}\u0000${desc}\u0000${amountCents}`;
}

/**
 * For each incoming row (in order), decide whether it is a duplicate.
 * The first `existing` occurrences of a key are marked as duplicates;
 * any occurrences beyond that are kept.
 */
export function markDuplicates<K>(keys: K[], existingCounts: Map<K, number>): boolean[] {
  const seen = new Map<K, number>();
  return keys.map(k => {
    const existing = existingCounts.get(k) ?? 0;
    const prior = seen.get(k) ?? 0;
    seen.set(k, prior + 1);
    return prior < existing;
  });
}

/** Count occurrences per key — used to build the existing-rows map. */
export function countKeys<K>(keys: K[]): Map<K, number> {
  const counts = new Map<K, number>();
  for (const k of keys) counts.set(k, (counts.get(k) ?? 0) + 1);
  return counts;
}
